/**
 * Select All Checkboxes Helper
 *
 * Manages "select all" and individual row checkbox behavior.
 * Supports:
 * - Selecting all rows
 * - Deselecting individual rows after select-all
 * - Tracking selected / unselected IDs efficiently
 * - Keeping header checkbox state in sync
 *
 * Design:
 * - `selectedAllToggle` indicates whether "select all" mode is active
 * - `selectedIds` stores exceptions (unchecked IDs when select-all is active,
 *   or checked IDs when select-all is inactive)
 * - `currentCbs` mirrors the visual checkbox state for rows
 *
 * @module SelectAllService
 */
export default {
  data() {
    return {
        /**
         * Indicates whether "select all" mode is active.
         * When true, all rows are considered selected unless excluded.
         * @type {boolean}
         */
        selectedAllToggle: false,

        /**
         * Header checkbox state.
         * Checked only when all rows are effectively selected.
         * @type {boolean}
         */
        selectedAllCb: false,

        /**
         * Stores selected or unselected row IDs depending on mode.
         * - When selectedAllToggle = false → selected IDs
         * - When selectedAllToggle = true  → unselected IDs
         * Using an array to ensure Vue reactivity works reliably.
         * @type {Array<string>}
         */
        selectedIds: [],

        /**
         * Tracks the visual checked state of each row checkbox.
         * Index-based to match row order.
         * @type {boolean[]}
         */
        currentCbs: [],
    };
  },

  methods: {
    /**
     * Toggles the "select all" checkbox.
     * Clears individual selections and updates all row checkbox states.
     *
     * @returns {void}
     */
    selectAll() {
      if (this.selectedAllCb) {
        this.selectedAllToggle = false;
        this.selectedIds = [];
        this.selectedAllCb = false;
      } else {
        this.selectedAllToggle = true;
        this.selectedIds = [];
        this.selectedAllCb = true;
      }

      this.currentCbs = Array(this.currentCbs.length).fill(this.selectedAllCb);
    },

    /**
     * Stores the initial checked state of a row checkbox.
     * Typically called when rows are rendered.
     *
     * @param {number} id - Row identifier
     * @returns {void}
     */
    storeCurretCbs(id) {
        this.currentCbs.push(this.isRowChecked(id));
    },

    /**
     * Toggles an individual row checkbox.
     * Updates selected IDs, row checkbox state, and header checkbox state.
     *
     * @param {number} id - Row identifier
     * @param {number} index - Row index
     * @returns {void}
     */
    selectToggle(id, index) {
      const idx = this.selectedIds.indexOf(id);
      if (idx > -1) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }

      this.currentCbs[index] = this.isRowChecked(id);
      this.selectedAllCb = this.selectedAllToggle && this.selectedIds.length === 0;
    },

    /**
     * Toggles an individual row checkbox with item count.
     * Handles auto-check/uncheck of selectAll based on all conditions:
     * 1. When have items and check all items → auto checked in selectAll
     * 2. When uncheck one item → uncheck selectAll
     * 3. When check selectAll → auto checked in all items
     *
     * @param {number} id - Row identifier
     * @param {number} index - Row index
     * @param {number} itemCount - Total number of items on current page
     * @returns {void}
     */
    selectToggleWithCount(id, index, itemCount) {
      const idx = this.selectedIds.indexOf(id);
      if (idx > -1) {
        this.selectedIds.splice(idx, 1);
      } else {
        this.selectedIds.push(id);
      }
      this.currentCbs[index] = this.isRowChecked(id);
      this.selectedAllCb = this.isAllSelected(itemCount);
    },

    /**
     * Checks if all items are selected based on current mode.
     *
     * @param {number} itemCount - Total number of items on current page
     * @returns {boolean} True if all items are effectively selected
     */
    isAllSelected(itemCount) {
      if (itemCount === 0) return false;
      if (this.selectedAllToggle) {
        return this.selectedIds.length === 0;
      } else {
        return this.selectedIds.length === itemCount;
      }
    },

    /**
     * Determines whether a row checkbox should be checked.
     *
     * @param {number} id - Row identifier
     * @returns {boolean} True if the row should be checked
     */
    isRowChecked(id) {
      return this.selectedAllToggle ? this.selectedIds.indexOf(id) === -1 : this.selectedIds.indexOf(id) > -1;
    },

    /**
     * Resets all selection states.
     * Clears select-all mode and individual selections.
     *
     * @returns {void}
     */
    resetSelectedAll() {
      this.selectedAllToggle = false;
      this.selectedAllCb = false;
      this.selectedIds = [];
    },

    /**
     * Checks whether the current selection is empty.
     *
     * @param {number} total - Total number of rows
     * @returns {boolean} True if no rows are effectively selected
     */
    isEmptySelected(total) {
      return this.selectedAllToggle ? this.selectedIds.length === total : this.selectedIds.length === 0;
    }
  }
};
